import { useEffect, useRef, useState } from "react";
import { SpeechBubbleToast } from "@ever-tutor/et-ui";

interface FixNamePronunciationToastProps {
  visible: boolean;
  blobBottom?: number;
  onFix: () => void;
  onClose: () => void;
}

const COUNTDOWN_SECONDS = 8;

const HeadphonesIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="18"
    height="18"
  >
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3zM3 19a2 2 0 0 0 2 2h1v-7H3z" />
  </svg>
);

const FixNamePronunciationToast = ({
  visible,
  blobBottom,
  onFix,
  onClose,
}: FixNamePronunciationToastProps) => {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [progressKey, setProgressKey] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!visible) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    setSecondsLeft(COUNTDOWN_SECONDS);
    setProgressKey((k) => k + 1);

    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SpeechBubbleToast
      open={visible}
      top={blobBottom}
      right={22}
      progressDurationS={COUNTDOWN_SECONDS}
      progressKey={progressKey}
      aria-label="Name pronunciation prompt"
    >
      <div
        style={{
          padding: "17px 17px 14px 17px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              flexShrink: 0,
              alignSelf: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#F1FBF6",
              border: "1px solid #DBEEE3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#16A46F",
            }}
          >
            <HeadphonesIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 800,
                fontSize: 14.5,
                color: "#1F2A2F",
                lineHeight: 1.35,
              }}
            >
              Is your tutor saying your name right?
            </p>
            <p
              style={{
                margin: "5px 0 0",
                fontSize: 13,
                fontWeight: 500,
                color: "#999",
              }}
            >
              {`Closing in ${secondsLeft}s`}
            </p>
          </div>
        </div>

        <button
          onClick={onFix}
          style={{
            width: "100%",
            padding: "11px 0",
            background: "#16A46F",
            color: "#fff",
            border: "none",
            fontFamily: "'Nunito', sans-serif",
            fontWeight: 800,
            fontSize: 14,
            borderRadius: 10,
            cursor: "pointer",
            boxShadow: "0 6px 18px -8px rgba(22,164,111,.55)",
            letterSpacing: 0.1,
          }}
        >
          Fix it
        </button>
      </div>
    </SpeechBubbleToast>
  );
};

export default FixNamePronunciationToast;
